import { useCallback, useEffect, useMemo } from 'react'
import { WORKFLOW_STEPS } from '../config/workflow'

function hasRequiredMapping(chart, mapping) {
  if (!chart?.dimensions) return false

  return chart.dimensions.every((dimension) => {
    if (!dimension.required) return true
    return (mapping?.[dimension.id]?.ids?.length || 0) > 0
  })
}

export default function useWorkflowState({
  currentStep,
  setCurrentStep,
  data,
  currentChart,
  mapping,
}) {
  const mappingComplete = useMemo(
    () => hasRequiredMapping(currentChart, mapping),
    [currentChart, mapping]
  )

  const unlockedSteps = useMemo(
    () => ({
      load: true,
      map: !!data,
      style: !!data && !!currentChart,
      export: !!data && !!currentChart && mappingComplete,
    }),
    [currentChart, data, mappingComplete]
  )

  useEffect(() => {
    if (unlockedSteps[currentStep]) {
      return
    }

    if (unlockedSteps.style) {
      setCurrentStep('style')
    } else if (unlockedSteps.map) {
      setCurrentStep('map')
    } else {
      setCurrentStep('load')
    }
  }, [currentStep, setCurrentStep, unlockedSteps])

  const workflowSteps = useMemo(
    () =>
      WORKFLOW_STEPS.map((step, index) => ({
        ...step,
        isUnlocked: unlockedSteps[step.id],
        isCompleted:
          index < WORKFLOW_STEPS.findIndex((candidate) => candidate.id === currentStep),
      })),
    [currentStep, unlockedSteps]
  )

  const canAdvance = useMemo(
    () => ({
      load: !!data,
      map: mappingComplete,
      style: !!data && !!currentChart && mappingComplete,
      export: false,
    }),
    [currentChart, data, mappingComplete]
  )

  const goToNextStep = useCallback(() => {
    const stepOrder = WORKFLOW_STEPS.map((step) => step.id)
    const currentIndex = stepOrder.indexOf(currentStep)
    const nextStep = stepOrder[currentIndex + 1]
    if (nextStep && unlockedSteps[nextStep]) {
      setCurrentStep(nextStep)
    }
  }, [currentStep, setCurrentStep, unlockedSteps])

  const goToPreviousStep = useCallback(() => {
    const stepOrder = WORKFLOW_STEPS.map((step) => step.id)
    const currentIndex = stepOrder.indexOf(currentStep)
    const previousStep = stepOrder[currentIndex - 1]
    if (previousStep) {
      setCurrentStep(previousStep)
    }
  }, [currentStep, setCurrentStep])

  return {
    mappingComplete,
    unlockedSteps,
    workflowSteps,
    canAdvance,
    goToNextStep,
    goToPreviousStep,
  }
}
